import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  getITMaintenancePlans, createITMaintenancePlan, updateITMaintenancePlan, deleteITMaintenancePlan,
  executeMaintenancePlan, getClients, getITAssets,
} from '../../lib/api';
import { getITErrorMessage } from '../../lib/itErrors';
import { assetDisplayName } from '../../components/it-assets';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Wrench, Plus, PencilSimple, Trash, Play, CalendarCheck, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MAINTENANCE_TYPES = [
  { code: 'preventivo', name: 'Preventivo' },
  { code: 'correctivo', name: 'Correctivo' },
  { code: 'predictivo', name: 'Predictivo' },
];

const MAINTENANCE_STATUSES = [
  { code: 'pendiente', name: 'Pendiente' },
  { code: 'ejecutado', name: 'Ejecutado' },
  { code: 'cancelado', name: 'Cancelado' },
];

const EMPTY_MAINTENANCE = {
  client_id: '', asset_id: '', maintenance_type: 'preventivo', scheduled_for: '', status: 'pendiente', notes: '',
};

const statusColor = (status) => ({
  pendiente: 'bg-yellow-500/20 text-yellow-400',
  ejecutado: 'bg-green-500/20 text-green-400',
  cancelado: 'bg-gray-500/20 text-gray-400',
}[status] || 'bg-gray-500/20 text-gray-400');

const toList = (data) => (Array.isArray(data) ? data : (data?.items || []));
const inputClass = 'bg-[#12141a] border-gray-700 text-white';

export default function MaintenancePage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clients, setClients] = useState([]);
  const [assets, setAssets] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState(EMPTY_MAINTENANCE);
  const [checklistText, setChecklistText] = useState('');

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterClient !== 'all') params.client_id = filterClient;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await getITMaintenancePlans(params);
      setRecords(toList(res.data));
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al cargar los mantenimientos'));
    } finally {
      setLoading(false);
    }
  }, [filterClient, filterStatus]);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { getClients().then((res) => setClients(toList(res.data))).catch(() => {}); }, []);

  const loadClientAssets = async (clientId) => {
    if (!clientId) { setAssets([]); return; }
    try {
      const res = await getITAssets({ client_id: clientId });
      setAssets(toList(res.data));
    } catch { setAssets([]); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_MAINTENANCE);
    setChecklistText('');
    setAssets([]);
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setForm({
      client_id: record.client_id || '',
      asset_id: record.asset_id,
      maintenance_type: record.maintenance_type || 'preventivo',
      scheduled_for: record.scheduled_for || '',
      status: record.status || 'pendiente',
      notes: record.notes || '',
    });
    setChecklistText((record.checklist || []).join('\n'));
    loadClientAssets(record.client_id);
    setShowModal(true);
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`¿Eliminar el mantenimiento de "${record.asset_name || 'activo'}"?`)) return;
    try {
      await deleteITMaintenancePlan(record._id);
      toast.success('Mantenimiento eliminado');
      loadRecords();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo eliminar'));
    }
  };

  const handleExecute = async (record) => {
    if (!window.confirm(`¿Marcar como ejecutado el mantenimiento de "${record.asset_name}"?`)) return;
    try {
      await executeMaintenancePlan(record._id);
      toast.success('Mantenimiento ejecutado');
      loadRecords();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo ejecutar'));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.asset_id) return toast.error('Selecciona el activo');
    const checklist = checklistText.split('\n').map((line) => line.trim()).filter(Boolean);
    setSaving(true);
    try {
      if (editing) {
        await updateITMaintenancePlan(editing._id, {
          maintenance_type: form.maintenance_type,
          scheduled_for: form.scheduled_for || null,
          status: form.status,
          notes: form.notes || null,
          checklist,
        });
        toast.success('Mantenimiento actualizado');
      } else {
        await createITMaintenancePlan({
          asset_id: form.asset_id,
          maintenance_type: form.maintenance_type,
          scheduled_for: form.scheduled_for || null,
          status: form.status,
          notes: form.notes || null,
          checklist,
        });
        toast.success('Mantenimiento programado');
      }
      setShowModal(false);
      loadRecords();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al guardar el mantenimiento'));
    } finally {
      setSaving(false);
    }
  };

  const typeName = (code) => MAINTENANCE_TYPES.find((type) => type.code === code)?.name || code;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Wrench className="text-[#E2B05E]" size={28} /> Mantenimiento
            </h1>
            <p className="text-gray-400 text-sm mt-1">Mantenimientos programados y ejecutados por activo</p>
          </div>
          <Button onClick={openCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
            <Plus size={18} className="mr-2" /> Nuevo Mantenimiento
          </Button>
        </div>

        <div className="flex flex-wrap gap-3">
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-full sm:w-[220px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos los clientes</SelectItem>
              {clients.map((client) => <SelectItem key={client._id} value={client._id}>{client.name || client.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos los estados</SelectItem>
              {MAINTENANCE_STATUSES.map((status) => <SelectItem key={status.code} value={status.code}>{status.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
            </div>
          ) : records.length === 0 ? (
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg text-center py-12 text-gray-500">
              <CalendarCheck size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay mantenimientos registrados</p>
            </div>
          ) : records.map((record) => (
            <div key={record._id} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <span className="text-white font-semibold">{record.asset_name || 'Activo'}</span>
                    <Badge className={statusColor(record.status)}>{record.status}</Badge>
                    <Badge className="bg-[#3AB8D6]/20 text-[#3AB8D6]">{typeName(record.maintenance_type)}</Badge>
                  </div>
                  <div className="text-sm text-gray-400">
                    {record.client_name && <span className="text-gray-300">{record.client_name} · </span>}
                    {record.scheduled_for ? `Programado: ${record.scheduled_for.slice(0, 10)}` : 'Sin fecha'}
                    {record.executed_at && ` · Ejecutado: ${record.executed_at.slice(0, 10)}`}
                  </div>
                  {record.checklist?.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">Checklist: {record.checklist.join(' · ')}</div>
                  )}
                  {record.notes && <div className="text-xs text-gray-500 mt-1">{record.notes}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {record.status === 'pendiente' && (
                    <Button size="sm" variant="outline" onClick={() => handleExecute(record)} className="border-green-500/40 text-green-400 text-xs">
                      <Play size={14} className="mr-1" /> Ejecutar
                    </Button>
                  )}
                  {record.status === 'ejecutado' && <CheckCircle size={20} className="text-green-400 mr-2" />}
                  <Button size="sm" variant="ghost" aria-label="Editar mantenimiento" onClick={() => openEdit(record)}>
                    <PencilSimple size={16} className="text-[#E2B05E]" />
                  </Button>
                  <Button size="sm" variant="ghost" aria-label="Eliminar mantenimiento" onClick={() => handleDelete(record)}>
                    <Trash size={16} className="text-red-400" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">{editing ? 'Editar Mantenimiento' : 'Nuevo Mantenimiento'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Cliente</Label>
                  <Select
                    value={form.client_id || undefined}
                    disabled={Boolean(editing)}
                    onValueChange={(value) => { setForm((prev) => ({ ...prev, client_id: value, asset_id: '' })); loadClientAssets(value); }}
                  >
                    <SelectTrigger className={inputClass}><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {clients.map((client) => <SelectItem key={client._id} value={client._id}>{client.name || client.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Activo *</Label>
                  <Select
                    value={form.asset_id || undefined}
                    disabled={Boolean(editing)}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, asset_id: value }))}
                  >
                    <SelectTrigger className={inputClass}><SelectValue placeholder={form.client_id ? 'Seleccionar activo' : 'Elige un cliente primero'} /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700 max-h-60">
                      {assets.map((asset) => <SelectItem key={asset._id} value={asset._id}>{assetDisplayName(asset)}</SelectItem>)}
                      {editing && !assets.some((asset) => asset._id === editing.asset_id) && (
                        <SelectItem value={editing.asset_id}>{editing.asset_name || 'Activo'}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Tipo</Label>
                  <Select value={form.maintenance_type} onValueChange={(value) => setForm((prev) => ({ ...prev, maintenance_type: value }))}>
                    <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {MAINTENANCE_TYPES.map((type) => <SelectItem key={type.code} value={type.code}>{type.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Fecha programada</Label>
                  <Input type="date" value={(form.scheduled_for || '').slice(0, 10)} onChange={(event) => setForm((prev) => ({ ...prev, scheduled_for: event.target.value }))} className={inputClass} />
                </div>
                {editing && (
                  <div>
                    <Label className="text-gray-300">Estado</Label>
                    <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                      <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1d23] border-gray-700">
                        {MAINTENANCE_STATUSES.map((status) => <SelectItem key={status.code} value={status.code}>{status.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-gray-300">Checklist (un paso por línea)</Label>
                <Textarea value={checklistText} onChange={(event) => setChecklistText(event.target.value)} className={inputClass} rows={4} placeholder={'Limpiar polvo\nActualizar firmware'} />
              </div>
              <div>
                <Label className="text-gray-300">Notas</Label>
                <Textarea value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">{saving ? 'Guardando…' : 'Guardar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
